// B2B Credit Applications Management
import { useState, useCallback, useMemo } from "react";
import {
  Landmark,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useCreditApplications,
  useApproveCredit,
  useRejectCredit,
} from "@/hooks/admin/useB2B";
import type {
  CreditApplication,
  CreditApplicationStatus,
} from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<CreditApplicationStatus, string> = {
  pending: "En attente",
  approved: "Approuvée",
  rejected: "Rejetée",
  under_review: "En révision",
};

const STATUS_COLORS: Record<CreditApplicationStatus, string> = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  under_review: "bg-blue-100 text-blue-700",
};

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

interface DetailModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly application: CreditApplication | null;
}

function CreditDetailModal({ open, onClose, application }: DetailModalProps) {
  if (!application) return null;

  const rows: readonly { label: string; value: string }[] = [
    { label: "Entreprise", value: application.company_name },
    {
      label: "Montant demande",
      value: `${application.requested_amount_fcfa.toLocaleString("fr-SN")} FCFA`,
    },
    { label: "Statut", value: STATUS_LABELS[application.status] },
    {
      label: "Soumis le",
      value: new Date(application.submitted_at).toLocaleDateString("fr-SN"),
    },
    {
      label: "Examine par",
      value: application.reviewed_by ?? "Non examine",
    },
    {
      label: "Examine le",
      value: application.reviewed_at
        ? new Date(application.reviewed_at).toLocaleDateString("fr-SN")
        : "---",
    },
    { label: "Notes", value: application.notes ?? "Aucune note" },
  ];

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Details de la demande de credit"
      size="md"
      footer={
        <Button variant="outline" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      <dl className="space-y-3">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-start justify-between gap-4 border-b border-stone-100 pb-2 last:border-0"
          >
            <dt className="text-sm font-medium text-stone-500">{r.label}</dt>
            <dd className="text-sm text-stone-900 text-right">{r.value}</dd>
          </div>
        ))}
      </dl>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Reject notes modal
// ---------------------------------------------------------------------------

interface RejectModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly application: CreditApplication | null;
  readonly onReject: (id: string, notes: string) => Promise<void>;
  readonly isRejecting: boolean;
}

function RejectModal({
  open,
  onClose,
  application,
  onReject,
  isRejecting,
}: RejectModalProps) {
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setNotes("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!application) return;
    if (!notes.trim()) {
      setError("Une note explicative est requise.");
      return;
    }
    setError(null);
    try {
      await onReject(application.id, notes);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [application, notes, onReject, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Rejeter la demande de crédit"
      size="sm"
      footer={
        <>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isRejecting}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isRejecting}
            className="gap-1.5 bg-red-600 hover:bg-red-700"
          >
            {isRejecting && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Rejeter
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-stone-600">
          Rejet de la demande de{" "}
          <span className="font-semibold">{application?.company_name}</span>{" "}
          pour{" "}
          <span className="font-semibold">
            {application?.requested_amount_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
          .
        </p>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Motif du rejet
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Expliquez le motif du rejet..."
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Credit() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string | null>>({
    status: null,
    date_from: null,
    date_to: null,
  });
  const [rejectingApp, setRejectingApp] = useState<CreditApplication | null>(
    null,
  );
  const [viewingApp, setViewingApp] = useState<CreditApplication | null>(null);

  const params = useMemo(
    () => ({
      status: (filters.status as CreditApplicationStatus) ?? undefined,
      date_from: filters.date_from ?? undefined,
      date_to: filters.date_to ?? undefined,
    }),
    [filters],
  );

  const { data, isLoading, isError, refetch } = useCreditApplications(params);
  const approveCredit = useApproveCredit();
  const rejectCredit = useRejectCredit();

  const applications = data?.applications ?? [];

  // Client-side search
  const filteredApplications = useMemo(() => {
    if (!search) return applications;
    const q = search.toLowerCase();
    return applications.filter((a) =>
      a.company_name.toLowerCase().includes(q),
    );
  }, [applications, search]);

  const kpiData = useMemo(() => {
    const pending = applications.filter((a) => a.status === "pending").length;
    const approved = applications.filter((a) => a.status === "approved").length;
    const rejected = applications.filter((a) => a.status === "rejected").length;
    const totalRequested = applications.reduce(
      (s, a) => s + a.requested_amount_fcfa,
      0,
    );
    const totalApproved = applications
      .filter((a) => a.status === "approved")
      .reduce((s, a) => s + a.requested_amount_fcfa, 0);
    const approvalRate =
      applications.length > 0
        ? (approved / applications.length) * 100
        : 0;
    return { pending, approved, rejected, totalRequested, totalApproved, approvalRate };
  }, [applications]);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
  }, []);

  const handleApprove = useCallback(
    (app: CreditApplication) => {
      approveCredit.mutate(app.id);
    },
    [approveCredit],
  );

  const handleRejectConfirm = useCallback(
    async (id: string, notes: string) => {
      await rejectCredit.mutateAsync({ id, notes });
      setRejectingApp(null);
    },
    [rejectCredit],
  );

  const filterConfigs = useMemo(
    () => [
      {
        key: "status",
        label: "Statut",
        type: "select" as const,
        options: [
          { value: "pending", label: "En attente" },
          { value: "approved", label: "Approuvée" },
          { value: "rejected", label: "Rejetée" },
          { value: "under_review", label: "En révision" },
        ],
      },
      { key: "date_from", label: "Du", type: "date" as const },
      { key: "date_to", label: "Au", type: "date" as const },
    ],
    [],
  );

  const columns: readonly ColumnDef<CreditApplication>[] = useMemo(
    () => [
      {
        id: "company_name",
        header: "Entreprise",
        cell: (a) => (
          <span className="font-medium text-stone-900">{a.company_name}</span>
        ),
      },
      {
        id: "requested_amount_fcfa",
        header: "Montant demande",
        cell: (a) => (
          <span className="text-sm font-medium text-stone-900">
            {a.requested_amount_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (a) => (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[a.status]}`}
          >
            {STATUS_LABELS[a.status]}
          </span>
        ),
      },
      {
        id: "submitted_at",
        header: "Soumis le",
        cell: (a) => (
          <span className="text-sm text-stone-500">
            {new Date(a.submitted_at).toLocaleDateString("fr-SN")}
          </span>
        ),
      },
      {
        id: "reviewed_by",
        header: "Examine par",
        cell: (a) => (
          <span className="text-sm text-stone-500">
            {a.reviewed_by ?? "---"}
          </span>
        ),
      },
      {
        id: "notes",
        header: "Notes",
        cell: (a) => (
          <span className="max-w-xs truncate text-sm text-stone-500">
            {a.notes ?? "---"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (a) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setViewingApp(a)}
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Voir details"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
            {(a.status === "pending" || a.status === "under_review") && (
              <>
                <button
                  type="button"
                  onClick={() => handleApprove(a)}
                  className="rounded p-1.5 text-stone-400 hover:bg-emerald-50 hover:text-emerald-600"
                  aria-label="Approuver"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setRejectingApp(a)}
                  className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
                  aria-label="Rejeter"
                >
                  <XCircle className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        ),
      },
    ],
    [handleApprove],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Landmark className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Credit B2B
            </h1>
            <p className="text-sm text-stone-500">
              Gestion des demandes de credit client
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <KPICard
            label="En attente"
            value={kpiData.pending}
            format="number"
            icon={<Clock className="h-4 w-4" />}
          />
          <KPICard
            label="Approuvées"
            value={kpiData.approved}
            format="number"
            icon={<CheckCircle2 className="h-4 w-4" />}
          />
          <KPICard
            label="Rejetées"
            value={kpiData.rejected}
            format="number"
            icon={<XCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Total demande"
            value={kpiData.totalRequested}
            format="currency"
          />
          <KPICard
            label="Total approuvé"
            value={kpiData.totalApproved}
            format="currency"
            icon={<Landmark className="h-4 w-4" />}
          />
          <KPICard
            label="Taux d'approbation"
            value={kpiData.approvalRate}
            format="percent"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par entreprise..."
          />
          <FilterBar
            filters={filterConfigs}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les demandes de credit.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Reessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && filteredApplications.length === 0 ? (
              <EmptyState
                icon={<Landmark className="h-6 w-6" />}
                title="Aucune demande de credit"
                description={
                  search || filters.status
                    ? "Aucune demande ne correspond aux filtres."
                    : "Les demandes de credit B2B apparaitront ici."
                }
              />
            ) : (
              <AdminDataTable
                data={filteredApplications}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      <RejectModal
        open={rejectingApp !== null}
        onClose={() => setRejectingApp(null)}
        application={rejectingApp}
        onReject={handleRejectConfirm}
        isRejecting={rejectCredit.isPending}
      />

      <CreditDetailModal
        open={viewingApp !== null}
        onClose={() => setViewingApp(null)}
        application={viewingApp}
      />
    </AdminLayout>
  );
}
